import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Gratis-Satisbite — Geschenk ab Warenwert, automatisch im Warenkorb.
 *
 * Staffel (Theme-Einstellungen fg_*): ab 50 € Warenwert 1 Riegel, ab 100 € 3.
 * Dieses Modul legt die passende Menge der Geschenk-Variante in den Korb und
 * nimmt sie wieder heraus, wenn der Korb unter die Schwelle fällt. Den Preis
 * auf 0 € setzt NICHT dieses Script, sondern die Discount Function
 * (chunky-discounts, Modus "free-gift" bzw. freeGift im Lager-Sale) — wer den
 * Riegel unter 50 € per Cart-API einlegt, zahlt ihn ganz normal.
 *
 * Schwelle = Warenwert VOR Rabatten, ohne die Gratis-Stücke selbst, ohne
 * Versand. Die Function rechnet exakt genauso (sie sieht andere Rabatte nicht,
 * darum geht "nach Rabatt" gar nicht sauber). Formel je Stufe, von oben:
 *   Σ(original_price × qty) − min(stufe.qty, vorhandene Geschenk-Stücke) × preis ≥ stufe.min
 *
 * Löscht der Kunde das Geschenk, legen wir es in dieser Sitzung nicht wieder
 * ein (sessionStorage) — anders als beim Kühlzuschlag ist das kein Zwang.
 * Reduzieren bei Unterschreiten der Schwelle machen wir trotzdem, aber nur um
 * die Stücke, die wir selbst hinzugefügt haben; gekaufte Stücke derselben
 * Variante bleiben unangetastet.
 *
 * Konfiguration kommt aus `window.__freeGift` (gerendert in snippets/scripts.liquid).
 */

const CONFIG = window.__freeGift || {};
const ENABLED = CONFIG.enabled === true;
const VARIANT_ID = Number(CONFIG.variantId) || 0;
const UNIT_PRICE = Number(CONFIG.price) || 0; // Cent
const AVAILABLE = CONFIG.available === true;
// Absteigend nach Schwelle sortiert: die höchste erreichte Stufe gewinnt.
const TIERS = (CONFIG.tiers || [])
  .map((t) => ({ min: Number(t.min) || 0, qty: Number(t.qty) || 0 }))
  .filter((t) => t.min > 0 && t.qty > 0)
  .sort((a, b) => b.min - a.min);

const STORAGE_KEY = 'fh_free_gift';
const SOURCE = 'free-gift';

if (VARIANT_ID && TIERS.length) {
  let busy = false;
  let pending = false;

  function loadState() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return {
        added: Math.max(0, Number(parsed?.added) || 0),
        dismissed: parsed?.dismissed === true,
      };
    } catch {
      return { added: 0, dismissed: false };
    }
  }

  function saveState(state) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* Private Mode o. ä. — dann eben ohne Gedächtnis. */
    }
  }

  /** @returns {Promise<any>} the /cart.js payload */
  async function getCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`cart.js ${response.status}`);
    }
    return response.json();
  }

  /** @param {any} cart */
  function giftLines(cart) {
    return cart.items.filter((item) => item.variant_id === VARIANT_ID);
  }

  /** Stückzahl der Geschenk-Variante im Korb (über alle Zeilen). */
  function giftQuantity(cart) {
    return giftLines(cart).reduce((sum, item) => sum + item.quantity, 0);
  }

  /**
   * Menge, die dem Kunden laut Staffel zusteht — dieselbe Rechnung wie in der
   * Discount Function. 0, wenn keine Stufe erreicht ist.
   */
  function entitledQuantity(cart) {
    const total = cart.items.reduce((sum, item) => sum + item.original_price * item.quantity, 0);
    const have = giftQuantity(cart);
    for (const tier of TIERS) {
      const units = Math.min(tier.qty, have);
      if (total - units * UNIT_PRICE >= tier.min) {
        return tier.qty;
      }
    }
    return 0;
  }

  /** @returns {Promise<boolean>} ob der Server die Änderung angenommen hat */
  async function addGift(quantity) {
    // Einzelform (NICHT `items: [...]`): die Array-Form stösst im Shop eine
    // Cart-Rebuild-Logik an (Satisbites-Mix-Box), die Zeilen verwirft.
    const response = await fetch(`${Theme.routes.cart_add_url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: VARIANT_ID, quantity }),
    });
    return response.ok;
  }

  /**
   * @param {string} key - the cart line key
   * @param {number} quantity - target quantity (0 removes the line)
   * @returns {Promise<boolean>}
   */
  async function setLineQuantity(key, quantity) {
    const response = await fetch(`${Theme.routes.cart_change_url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    return response.ok;
  }

  /**
   * Bringt die Geschenk-Stücke mit der Staffel in Deckung.
   * @param {boolean} external - Auslöser war eine fremde Warenkorbänderung
   *   (Kunde/Theme), nicht unser eigener Nachlauf. Nur dann kann ein Löschen
   *   des Geschenks erkannt werden.
   */
  async function reconcileOnce(external) {
    let cart = await getCart();
    const state = loadState();
    const have = giftQuantity(cart);

    // Weniger Stücke als wir eingelegt haben ⇒ der Kunde hat das Geschenk
    // (teilweise) entfernt. Merken, nicht wieder aufdrängen.
    if (external && have < state.added) {
      state.dismissed = true;
      state.added = have;
    }

    let desired = ENABLED && AVAILABLE ? entitledQuantity(cart) : 0;
    // Fällt der Korb unter die erste Schwelle, ist das "Nein" verbraucht: wer
    // später wieder qualifiziert, bekommt das Geschenk erneut angeboten. Sonst
    // bliebe ein einmal entferntes Geschenk die ganze Sitzung lang weg.
    if (desired === 0 && state.dismissed) {
      state.dismissed = false;
    }
    if (state.dismissed) {
      desired = Math.min(desired, state.added);
    }

    const delta = desired - state.added;
    let changed = false;

    if (delta > 0) {
      const lines = giftLines(cart);
      const ok = lines.length
        ? await setLineQuantity(lines[0].key, lines[0].quantity + delta)
        : await addGift(delta);
      if (ok) {
        state.added += delta;
        changed = true;
      }
    } else if (delta < 0) {
      // Nur unsere eigenen Stücke zurücknehmen, von der ersten Zeile.
      const lines = giftLines(cart);
      if (lines.length) {
        const line = lines[0];
        const target = Math.max(0, line.quantity + delta);
        const ok = await setLineQuantity(line.key, target);
        if (ok) {
          state.added = Math.max(0, state.added - (line.quantity - target));
          changed = true;
        }
      } else {
        state.added = 0;
      }
    }

    saveState(state);

    if (changed) {
      cart = await getCart();
      // Ohne `sections` rendert sich jede cart-items-component selbst neu,
      // cart-icon liest data.itemCount.
      document.dispatchEvent(
        new CartUpdateEvent(cart, SOURCE, {
          source: SOURCE,
          itemCount: cart.item_count,
        })
      );
    }
  }

  async function reconcile() {
    if (busy) {
      pending = true;
      return;
    }
    busy = true;
    try {
      let external = true;
      do {
        pending = false;
        await reconcileOnce(external);
        external = true;
      } while (pending);
    } catch (error) {
      console.error('[free-gift]', error);
    } finally {
      busy = false;
    }
  }

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (event.detail?.data?.source === SOURCE) return;
    reconcile();
  });

  // Fallback-Knopf in der Bar (snippets/free-gift-bar.liquid): erscheint nur,
  // wenn dem Kunden Stücke zustehen, die nicht im Korb liegen — also nach einem
  // Entfernen oder wenn der Auto-Add nicht durchkam. Der Klick hebt die Sperre
  // auf und lässt den Automatismus den Rest machen; der hat weiter Vorrang.
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-fg-add]') : null;
    if (!button) return;
    event.preventDefault();
    button.setAttribute('disabled', '');
    const state = loadState();
    state.dismissed = false;
    saveState(state);
    reconcile().finally(() => button.removeAttribute('disabled'));
  });

  // Zurück aus dem Back-Forward-Cache oder Tab wieder sichtbar: der Korb kann
  // sich in einem anderen Tab geändert haben.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) reconcile();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') reconcile();
  });

  if (document.readyState !== 'loading') {
    reconcile();
  } else {
    document.addEventListener('DOMContentLoaded', reconcile, { once: true });
  }
}
